import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons"
import { Box, Flex, Text, Tooltip, useOutsideClick } from "@chakra-ui/react"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useAppSelector } from "@/store"
import { selectIsChromiumBlinkBrowser } from "@/stores/app_slice"
import { selectIsFullScreen } from "@/stores/remote_desk_state_slice"
import { selectMousePos } from "@/stores/mouse_slice"
import { getOS, OS } from "@/utils/os"
import { mytest } from "@/utils/tools"

interface IconButtonProps {
  src: string
  id?: string
  size?: string
  width?: string
  height?: string
  padding?: string
  fontSize?: string
  onClick: () => void
  textString?: string
}
export const AppIconButton: React.FC<IconButtonProps> = (props) => {
  const { src, width, height, size = 24, padding = 4, onClick } = props
  return (
    <img
      src={src}
      style={{
        width: width ?? size,
        height: height ?? size,
        padding,
        cursor: "pointer",
        color: "#fff",
      }}
      onClick={onClick}
    />
  )
}

export const AppIconTextButton: React.FC<IconButtonProps> = (props) => {
  const {
    id,
    src,
    width,
    height,
    size = 24,
    padding = 4,
    fontSize = "12px",
    onClick,
    textString,
  } = props
  return (
    <Flex
      id={id}
      direction="column"
      display="flex"
      justifyContent="center"
      alignItems="center"
      cursor="pointer"
      onClick={onClick}
    >
      <Box>
        <img
          src={src}
          style={{
            width: width ?? size,
            height: height ?? size,
            padding,
            cursor: "pointer",
            color: "#fff",
          }}
        />
        <Box color="#ffffffe0" fontSize={fontSize}>
          {textString}
        </Box>
      </Box>
    </Flex>
  )
}

export interface AppSelectOption {
  label: string
  value: any
}

interface AppSelectProps<Option = any> {
  value?: Option
  options: AppSelectOption[]
  onChange: (option: AppSelectOption) => void
  triggerStyle?: React.CSSProperties
}

// 自定义滚动条组件（用于全屏模式下虚拟鼠标可以交互）
interface CustomScrollbarProps {
  contentRef: React.RefObject<HTMLDivElement>
  maxHeight: number
  isFullScreen: boolean
}

const CustomScrollbar: React.FC<CustomScrollbarProps> = ({
  contentRef,
  maxHeight,
  isFullScreen,
}) => {
  const [thumbHeight, setThumbHeight] = useState(0)
  const [thumbTop, setThumbTop] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [showScrollbar, setShowScrollbar] = useState(false)
  const dragStartY = useRef(0)
  const dragStartScrollTop = useRef(0)
  const trackRef = useRef<HTMLDivElement>(null)

  // 获取虚拟鼠标位置（用于全屏模式下拖拽）
  const virtualMousePos = useAppSelector(selectMousePos)
  // 使用 ref 存储最新的虚拟鼠标位置，避免作为 useEffect 依赖项导致频繁重新运行
  const virtualMousePosRef = useRef(virtualMousePos)
  useEffect(() => {
    virtualMousePosRef.current = virtualMousePos
  }, [virtualMousePos])

  // 计算滚动条 thumb 的高度和位置
  const updateScrollbar = useCallback(() => {
    const content = contentRef.current
    if (!content) return

    const { scrollHeight, clientHeight, scrollTop } = content
    // 只有内容超出容器时才显示滚动条
    if (scrollHeight <= clientHeight) {
      setShowScrollbar(false)
      return
    }

    setShowScrollbar(true)
    // thumb 高度 = (可视高度 / 内容高度) * 轨道高度
    const trackHeight = maxHeight
    const newThumbHeight = Math.max(
      (clientHeight / scrollHeight) * trackHeight,
      20
    ) // 最小高度 20px
    setThumbHeight(newThumbHeight)

    // thumb 位置 = (滚动位置 / 可滚动距离) * (轨道高度 - thumb高度)
    const scrollableDistance = scrollHeight - clientHeight
    const thumbTrackSpace = trackHeight - newThumbHeight
    const newThumbTop =
      scrollableDistance > 0
        ? (scrollTop / scrollableDistance) * thumbTrackSpace
        : 0
    setThumbTop(newThumbTop)
  }, [contentRef, maxHeight])

  // 监听内容滚动
  useEffect(() => {
    const content = contentRef.current
    if (!content) return

    const handleScroll = () => updateScrollbar()
    content.addEventListener("scroll", handleScroll)

    // 初始化
    updateScrollbar()

    // 监听内容变化（使用 ResizeObserver）
    const resizeObserver = new ResizeObserver(() => {
      updateScrollbar()
    })
    resizeObserver.observe(content)

    return () => {
      content.removeEventListener("scroll", handleScroll)
      resizeObserver.disconnect()
    }
  }, [contentRef, updateScrollbar])

  // 点击轨道进行翻页滚动
  const handleTrackClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    const content = contentRef.current
    const track = trackRef.current
    if (!content || !track) return

    const trackRect = track.getBoundingClientRect()
    const clickY = e.clientY - trackRect.top
    const thumbCenter = thumbTop + thumbHeight / 2

    // 点击 thumb 上方则向上翻页，下方则向下翻页
    const pageScrollAmount = content.clientHeight * 0.8
    if (clickY < thumbCenter) {
      content.scrollBy({ top: -pageScrollAmount, behavior: "smooth" })
    } else {
      content.scrollBy({ top: pageScrollAmount, behavior: "smooth" })
    }
  }

  // 开始拖拽 thumb
  const handleThumbMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    setIsDragging(true)
    // 直接使用事件的 clientY
    // 全屏模式下：这是合成事件，clientY 已经是正确的虚拟鼠标位置
    // 非全屏模式下：这是原生事件，clientY 也是正确的
    dragStartY.current = e.clientY
    dragStartScrollTop.current = contentRef.current?.scrollTop ?? 0
  }

  // 拖拽过程中的移动
  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (e: MouseEvent) => {
      const content = contentRef.current
      if (!content) return

      // 全屏模式下：
      // - 原生事件（isTrusted: true）：忽略，因为 pointer lock 下 clientY 无效
      // - 合成事件（isTrusted: false）：处理，clientY 是正确的虚拟鼠标位置
      if (isFullScreen && e.isTrusted) {
        return
      }

      // 直接使用事件的 clientY
      // 合成事件的 clientY 已经是正确的虚拟鼠标位置
      const currentY = e.clientY

      const deltaY = currentY - dragStartY.current
      const trackHeight = maxHeight
      const thumbTrackSpace = trackHeight - thumbHeight

      // 计算移动的滚动距离
      const scrollableDistance = content.scrollHeight - content.clientHeight
      const scrollDelta =
        thumbTrackSpace > 0
          ? (deltaY / thumbTrackSpace) * scrollableDistance
          : 0

      content.scrollTop = dragStartScrollTop.current + scrollDelta
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    document.addEventListener("mousemove", handleMouseMove)
    document.addEventListener("mouseup", handleMouseUp)

    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
    }
  }, [isDragging, contentRef, maxHeight, thumbHeight, isFullScreen])

  if (!showScrollbar) return null

  return (
    <Box
      id="full-screen-unvideo-element-tag"
      ref={trackRef}
      position="absolute"
      right="2px"
      top="0"
      width="8px"
      height={`${maxHeight}px`}
      backgroundColor="rgba(255, 255, 255, 0.1)"
      borderRadius="4px"
      cursor="pointer"
      onClick={handleTrackClick}
      sx={{
        "&.virtual-hover": { backgroundColor: "rgba(255, 255, 255, 0.15)" },
      }}
      _hover={{ backgroundColor: "rgba(255, 255, 255, 0.15)" }}
    >
      {/* Scrollbar Thumb */}
      <Box
        id="full-screen-unvideo-element-tag"
        position="absolute"
        top={`${thumbTop}px`}
        left="0"
        width="100%"
        height={`${thumbHeight}px`}
        backgroundColor={
          isDragging ? "rgba(255, 255, 255, 0.6)" : "rgba(255, 255, 255, 0.4)"
        }
        borderRadius="4px"
        cursor="grab"
        onMouseDown={handleThumbMouseDown}
        // 阻止 click 事件冒泡到 track，防止触发 handleTrackClick 导致翻页滚动
        // 在全屏模式下，dispatchVirtualMouseUp 会派发合成 click 事件，如果不阻止会导致滚动条跳动
        onClick={(e) => e.stopPropagation()}
        sx={{
          "&.virtual-hover": { backgroundColor: "rgba(255, 255, 255, 0.5)" },
          "&:active": { cursor: "grabbing" },
        }}
        _hover={{ backgroundColor: "rgba(255, 255, 255, 0.5)" }}
      />
    </Box>
  )
}

export const AppSelect: React.FC<AppSelectProps> = (props) => {
  const { value, options, onChange, triggerStyle } = props
  const [selectedValue, setSelectedValue] = useState(value ?? null)
  const [isOpen, setIsOpen] = useState(false)
  const ref = React.useRef<HTMLDivElement>(null)
  const dropdownContentRef = useRef<HTMLDivElement>(null)
  const labelRef = useRef<HTMLParagraphElement>(null)
  // label 文本溢出（被截断成 ...）时才显示 tooltip 展示完整名称
  const [isLabelTruncated, setIsLabelTruncated] = useState(false)
  const DROPDOWN_MAX_HEIGHT = 210

  // 受控 value 变化或下拉开关时，重新检测 label 是否溢出
  useEffect(() => {
    const el = labelRef.current
    if (el === null) {
      setIsLabelTruncated(false)
      return
    }
    setIsLabelTruncated(el.scrollWidth > el.clientWidth)
  }, [selectedValue?.label, isOpen])

  // 选择项可能由异步协议返回（例如缩放比）。保持展示内容与受控 value 同步。
  useEffect(() => {
    setSelectedValue(value ?? null)
  }, [value?.label])

  // 检测是否为全屏模式
  const isFullScreen = useAppSelector(selectIsFullScreen)
  const isChromiumBlinkVal = useAppSelector(selectIsChromiumBlinkBrowser)
  const isMac = getOS() === OS.MacOS
  const isMacFullScreen = isFullScreen && isMac && mytest && isChromiumBlinkVal

  useOutsideClick({
    ref,
    handler: () => setIsOpen(false),
  })

  const handleDropdownClick = () => {
    setIsOpen(!isOpen)
  }

  const handleSelect = (value: AppSelectOption) => {
    setSelectedValue(value)
    onChange(value)
    setIsOpen(false)
  }

  const buildItem = (option: AppSelectOption): React.ReactNode => {
    const isSelected = selectedValue?.label === option.label
    return (
      <Flex
        key={option.label}
        padding="8px"
        cursor="pointer"
        alignItems={"center"}
        paddingY={"6px"}
        _hover={{ backgroundColor: "grey" }}
        sx={{
          // 全屏模式下虚拟鼠标 hover 时的样式（CSS :hover 无法被合成事件触发）
          "&.virtual-hover": { backgroundColor: "grey" },
        }}
        onClick={() => handleSelect(option)}
      >
        <Text color={"#fcfcfce8"} flex={1} textAlign={"left"} fontSize={"13px"}>
          {option.label}
        </Text>
        {isSelected ? (
          <CheckIcon color="white" width={"10px"} height={"10px"} />
        ) : null}
      </Flex>
    )
  }

  return (
    <Box ref={ref} position="relative" width="100%">
      <Flex
        alignItems={"center"}
        style={triggerStyle}
        cursor="pointer"
        onClick={handleDropdownClick}
      >
        <Tooltip
          label={selectedValue?.label ?? ""}
          isDisabled={!isLabelTruncated}
          placement="top"
          hasArrow
        >
          <Text
            ref={labelRef}
            flex={1}
            color={"white"}
            textAlign={"right"}
            fontSize={"13px"}
            noOfLines={1}
            title={isLabelTruncated ? selectedValue?.label : undefined}
          >
            {selectedValue?.label ?? ""}
          </Text>
        </Tooltip>
        <Box paddingRight="8px" display="flex" alignItems="center">
          {isOpen ? (
            <ChevronUpIcon color={"white"} boxSize={5} />
          ) : (
            <ChevronDownIcon color={"white"} boxSize={5} />
          )}
        </Box>
      </Flex>
      {isOpen && (
        <Box
          position="absolute"
          top="100%"
          width="100%"
          bg="black"
          border="1px solid #ffffffd9"
          borderRadius="8px"
          // 下拉菜单必须覆盖设置页中的 Slider 等交互组件。
          zIndex="20"
          marginTop={"4px"}
          overflow="hidden"
        >
          {/* 下拉内容区域 */}
          <Flex
            ref={dropdownContentRef}
            id="full-screen-unvideo-element-tag"
            maxHeight={`${DROPDOWN_MAX_HEIGHT}px`}
            overflowY="auto"
            direction={"column"}
            // 全屏模式：为自定义滚动条留出空间；非全屏模式：不需要额外空间
            paddingRight={isMacFullScreen ? "10px" : "0"}
            sx={
              isMacFullScreen
                ? {
                    // 全屏模式：隐藏原生滚动条，使用自定义滚动条
                    "&::-webkit-scrollbar": { display: "none" },
                    scrollbarWidth: "none", // Firefox
                    msOverflowStyle: "none", // IE/Edge
                  }
                : {}
            }
          >
            {options.map(buildItem)}
          </Flex>
          {/* 全屏模式下使用自定义滚动条 */}
          {isMacFullScreen && (
            <CustomScrollbar
              contentRef={dropdownContentRef}
              maxHeight={DROPDOWN_MAX_HEIGHT}
              isFullScreen={isMacFullScreen}
            />
          )}
        </Box>
      )}
    </Box>
  )
}
